import { Chip, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

import { BUCKET_LABEL, humanise, kindOf, quickPlots } from "../lib/spec.js";

/**
 * What to plot it by, one click from the answer.
 *
 * A count of something is one figure, and what a person wants next is usually the same question across dates, or
 * across a kind of thing: "how many over 10k", and then by month, or by status. That is the first grouping of the
 * form and it is set here without opening the form's sections: pick a field, and the answer runs again as a chart.
 * Picking the one that is already picked takes it off and the figure comes back. A date has a bucket (day to year)
 * and anything that is not the first thing plotted can be what the bars are split by.
 *
 * Nothing here is a second source of truth: it reads and writes the form's own `dimensions`, so what it does is
 * what the Break it down section would have done, and what the kernel is asked is the same spec either way.
 */
export default function PlotBar({ fields, dimensions, onChange }) {
  const picks = quickPlots(fields);
  if (!picks.length) return null;
  const first = dimensions[0]?.field || "";
  const second = dimensions[1]?.field || "";
  const firstField = fields.find((f) => f.name === first);
  const shown = picks.slice(0, 6);
  const rest = picks.slice(6);
  const choose = (name) => {
    if (!name || name === first) return onChange([]);
    const field = fields.find((f) => f.name === name);
    const head = { field: name, bucket: kindOf(field) === "time" ? (dimensions[0]?.bucket || "month") : "" };
    onChange(second && second !== name ? [head, dimensions[1]] : [head]);
  };
  return (
    <Stack sx={{ gap: 1 }} component="section" aria-label="Plot it by">
      <Typography variant="subtitle2" component="h3">Plot it by</Typography>
      <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        {shown.map((f) => (
          <Chip
            key={f.name} clickable size="small" label={`${humanise(f.name)}${kindOf(f) === "time" ? " (date)" : ""}`}
            color={f.name === first ? "primary" : "default"} variant={f.name === first ? "filled" : "outlined"}
            aria-pressed={f.name === first} onClick={() => choose(f.name)}
          />
        ))}
        {rest.length ? (
          <TextField
            select size="small" label="More" value={rest.some((f) => f.name === first) ? first : ""}
            slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }} sx={{ minWidth: 150 }}
            onChange={(e) => choose(e.target.value)}
          >
            <MenuItem value="">Other fields</MenuItem>
            {rest.map((f) => <MenuItem key={f.name} value={f.name}>{humanise(f.name)}</MenuItem>)}
          </TextField>
        ) : null}
      </Stack>
      {firstField && kindOf(firstField) === "time" ? (
        <ToggleButtonGroup
          exclusive size="small" aria-label="Group dates by" value={dimensions[0]?.bucket || "month"}
          onChange={(_, v) => v && onChange([{ field: first, bucket: v }, ...(dimensions[1] ? [dimensions[1]] : [])])}
        >
          {Object.entries(BUCKET_LABEL).map(([k, v]) => <ToggleButton key={k} value={k}>{humanise(v)}</ToggleButton>)}
        </ToggleButtonGroup>
      ) : null}
      {first ? (
        <TextField
          select size="small" label="Split each by" value={second}
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }} sx={{ maxWidth: 240 }}
          onChange={(e) => onChange(e.target.value ? [dimensions[0], { field: e.target.value, bucket: "" }] : [dimensions[0]])}
        >
          <MenuItem value="">Nothing: one series</MenuItem>
          {picks.filter((f) => f.name !== first && kindOf(f) !== "time").map((f) => (
            <MenuItem key={f.name} value={f.name}>{humanise(f.name)}</MenuItem>
          ))}
        </TextField>
      ) : null}
    </Stack>
  );
}
